import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { EmployeeProfile } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/fetchEmployeeProfiles/types";
import {
  deleteEmployeeProfileAction,
  fetchEmployeeProfilesAction,
} from "@/modules/administration/features/EmployeeDirectoryManagement/slices/employeeProfileSlice";
import { useNotification } from "@/context/NotificationContext";

export const useEmployeeProfileManagementPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const list = useAppSelector((state: RootState) => state.employeeProfile.list);
  const loading = useAppSelector(
    (state: RootState) => state.employeeProfile.loadingList,
  );
  const error = useAppSelector(
    (state: RootState) => state.employeeProfile.listError,
  );

  const [deleteTarget, setDeleteTarget] = useState<EmployeeProfile | null>(
    null,
  );

  const refetch = useCallback(() => {
    dispatch(fetchEmployeeProfilesAction());
  }, [dispatch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const goToCreate = useCallback(() => {
    navigate("/admin/employee-directory/profiles/create");
  }, [navigate]);

  const goToEdit = useCallback(
    (profile: EmployeeProfile) => {
      navigate(`/admin/employee-directory/profiles/${profile.uuid}/edit`);
    },
    [navigate],
  );

  const openDeleteModal = useCallback(
    (profile: EmployeeProfile) => setDeleteTarget(profile),
    [],
  );
  const closeDeleteModal = useCallback(() => setDeleteTarget(null), []);

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    dispatch(
      deleteEmployeeProfileAction(deleteTarget.uuid, () => {
        showNotification?.(
          "Success",
          "Employee profile deleted successfully.",
          "success",
        );
        closeDeleteModal();
        refetch();
      }),
    );
  }, [dispatch, deleteTarget, closeDeleteModal, refetch, showNotification]);

  return {
    list,
    loading,
    error,
    deleteTarget,
    goToCreate,
    goToEdit,
    openDeleteModal,
    closeDeleteModal,
    handleDelete,
  };
};
